import { ScaleElement } from '@scale/base-element';
import { activateScripts, fetchTextWithTimeout, parseSectionHTML } from '@scale/utilities';

class ScaleProductRecommendations extends ScaleElement {
  #loaded = false;
  #controller;
  #observer;

  setup() {
    if (this.querySelector('.product-recommendations__grid, .product-recommendations__slider')) {
      this.#loaded = true;
      this.removeAttribute('loading');
      this.removeAttribute('aria-busy');
      return;
    }
    if (this.hasAttribute('data-visual-preview-mode')) {
      this.#loadRecommendations();
      return;
    }

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.#observer.disconnect();
      this.#loadRecommendations();
    }, { rootMargin: '800px' });
    this.#observer.observe(this);
  }

  teardown() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  async #loadRecommendations() {
    const url = this.dataset.url;
    if (!url) {
      this.#settleHidden();
      return;
    }

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;

    try {
      const { response, text: html } = await fetchTextWithTimeout(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = parseSectionHTML(html);
      const recommendations = doc.querySelector('scale-product-recommendations');
      const content = recommendations?.querySelector(
        '.product-recommendations__grid, .product-recommendations__slider'
      );

      if (!content || this.#controller !== controller) {
        if (this.#controller === controller) this.#settleHidden();
        return;
      }

      this.replaceChildren(...recommendations.childNodes);
      activateScripts(this);
      this.#loaded = true;
      this.removeAttribute('loading');
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (this.#controller === controller) this.#settleHidden();
    } finally {
      if (this.#controller === controller) this.#controller = null;
    }
  }

  #settleHidden() {
    this.#loaded = true;
    if (!this.hasAttribute('data-visual-preview-mode')) this.hidden = true;
    this.removeAttribute('loading');
    this.removeAttribute('aria-busy');
  }
}

if (!customElements.get('scale-product-recommendations')) {
  customElements.define('scale-product-recommendations', ScaleProductRecommendations);
}
